import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../api/config';
import '../styles/memory.css';
import '../styles/knowledge.css';

/**
 * Sidebar button + panel: what the chatbot knows. Per model year: the manuals, domains and chapters it has
 * read, the size of its knowledge graph, its most connected parts and DTCs, and questions to try (one click
 * sets the vehicle and asks). Data: GET knowledge/catalog.
 */

const UI_MODEL = { SuperGreat: 'Super Great' };
export const uiModel = (m) => UI_MODEL[m] || m;
const DOMAIN_COLORS = { chassis: '#2563eb', electrical: '#d97706', 'electronic-control': '#7c3aed', engine: '#dc2626', 'ev-system': '#0d9488' };
const KIND_COLORS = { dtc: '#ea580c', location: '#0d9488', wiring: '#d97706', procedure: '#2563eb' };
const fmt = (n) => Number(n || 0).toLocaleString();

const CAPABILITIES = [
  ['DTC diagnosis', 'Meaning, set conditions, suspected parts and the inspection flow for a trouble code'],
  ['Wiring & circuits', 'Connector pins, wire colours, power supply, ground and signal paths'],
  ['Installation & location', 'Where a part is mounted, removal / installation steps and tightening torques'],
  ['Specifications', 'Resistance, voltage, pressure, clearance and capacity values from the spec tables'],
  ['Manual pages', 'Every answer links the exact manual pages, with page images you can open'],
  ['Japanese & English', 'Ask in either language; the manuals are read in both'],
  ['Follow-ups & memory', 'Remembers what each conversation is about, so “it” and “that connector” work'],
];

function useCatalog(open) {
  const [state, setState] = useState({ loading: false, error: null, data: null });
  useEffect(() => {
    if (!open || state.data || state.loading) return;
    setState({ loading: true, error: null, data: null });
    axios.get(apiUrl('knowledge/catalog'))
      .then((r) => setState({ loading: false, error: null, data: r.data }))
      .catch((e) => setState({ loading: false, error: e.message, data: null }));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

function Stat({ label, value, color }) {
  return (
    <div className="kn-stat" style={{ '--kn-c': color }}>
      <div className="kn-stat__value">{fmt(value)}</div>
      <div className="kn-stat__label">{label}</div>
    </div>
  );
}

function SetView({ set, onAsk, t }) {
  const [filter, setFilter] = useState('');
  const maxCh = Math.max(1, ...set.chapters.map((c) => c.pages));
  const chapters = set.chapters.filter((c) => !filter || `${c.code} ${c.name}`.toLowerCase().includes(filter.toLowerCase()));
  const g = set.graph || {};
  return (
    <div className="kn-set">
      <div className="kn-stats">
        <Stat label={t('Manual pages')} value={set.pages} color="#2563eb" />
        <Stat label={t('Pages with wiring data')} value={set.wiring_pages} color="#d97706" />
        <Stat label={t('Pages with specifications')} value={set.spec_pages} color="#0d9488" />
        <Stat label={t('DTCs')} value={g.DTC} color="#ea580c" />
        <Stat label={t('Components')} value={g.Component} color="#7c3aed" />
        <Stat label={t('Connectors')} value={g.Connector} color="#0891b2" />
        <Stat label={t('Locations')} value={g.Location} color="#65a30d" />
        <Stat label={t('Sections')} value={g.Section} color="#64748b" />
      </div>

      <section className="kn-block">
        <div className="cv-section-label">{t('Domains')}</div>
        <div className="kn-bar" role="img" aria-label={set.domains.map((d) => `${d.label} ${d.pages}`).join(', ')}>
          {set.domains.map((d) => (
            <span key={d.key} style={{ flexGrow: d.pages, background: DOMAIN_COLORS[d.key] || '#94a3b8' }} title={`${d.label}: ${fmt(d.pages)} pages`} />
          ))}
        </div>
        <div className="kn-bar-legend">
          {set.domains.map((d) => (
            <span key={d.key}><i style={{ background: DOMAIN_COLORS[d.key] || '#94a3b8' }} />{d.label} <b>{fmt(d.pages)}</b></span>
          ))}
        </div>
      </section>

      <section className="kn-block">
        <div className="cv-section-label">{t('Try asking')}</div>
        <div className="kn-asks">
          {set.examples.map((e) => (
            <button key={e.q} type="button" className="kn-ask" style={{ '--kn-c': KIND_COLORS[e.kind] || '#2563eb' }} onClick={() => onAsk(set, e.q)}>
              <span className="kn-ask__kind">{e.kind}</span>
              <span>{e.q}</span>
            </button>
          ))}
        </div>
      </section>

      <div className="kn-cols">
        <section className="kn-block">
          <div className="kn-block__head">
            <div className="cv-section-label">{t('Chapters')} ({set.chapters.length})</div>
            <input className="kn-filter" placeholder={t('Filter chapters…')} value={filter} onChange={(e) => setFilter(e.target.value)} />
          </div>
          <div className="kn-chapters">
            {chapters.map((c) => (
              <div key={c.code} className="kn-chapter" title={`${c.code} ${c.name} — ${fmt(c.pages)} pages`}>
                <span className="kn-chapter__code">{c.code}</span>
                <span className="kn-chapter__name">{c.name || '—'}</span>
                <span className="kn-chapter__meter"><span style={{ width: `${Math.max(4, (100 * c.pages) / maxCh)}%` }} /></span>
                <span className="kn-chapter__n">{fmt(c.pages)}</span>
              </div>
            ))}
          </div>
        </section>
        <div className="kn-side">
          <section className="kn-block">
            <div className="cv-section-label">{t('Manuals')} ({set.manuals.length})</div>
            <ul className="kn-list">
              {set.manuals.map((m) => (
                <li key={m.name}><span className="kn-dot" style={{ background: DOMAIN_COLORS[Object.keys(DOMAIN_COLORS).find((k) => m.domain.toLowerCase().startsWith(k.split('-')[0])) ] || '#94a3b8' }} />{m.label}<b>{fmt(m.pages)}</b></li>
              ))}
            </ul>
          </section>
          <section className="kn-block">
            <div className="cv-section-label">{t('Most connected parts')}</div>
            <ul className="kn-list">
              {set.top_components.slice(0, 6).map((c) => (
                <li key={c.code}><code>{c.code}</code>{c.name}<b>{fmt(c.links)}</b></li>
              ))}
            </ul>
          </section>
          <section className="kn-block">
            <div className="cv-section-label">{t('DTCs with the most suspected parts')}</div>
            <ul className="kn-list">
              {set.top_dtcs.slice(0, 5).map((d) => (
                <li key={d.code} title={d.description}><code className="kn-dtc">{d.code}</code><span className="kn-ellipsis">{d.description}</span><b>{d.suspects}</b></li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

function Panel({ onClose, onAsk, selectedModel, selectedYear, t }) {
  const { loading, error, data } = useCatalog(true);
  const sets = useMemo(() => (data && data.sets) || [], [data]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    if (!sets.length || active) return;
    const match = sets.find((s) => uiModel(s.model) === selectedModel && (!selectedYear || s.year === selectedYear));
    setActive(`${(match || sets[0]).model}|${(match || sets[0]).year}`);
  }, [sets, active, selectedModel, selectedYear]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const set = sets.find((s) => `${s.model}|${s.year}` === active);
  const tot = data && data.totals;

  return (
    <div className="mg-overlay" role="dialog" aria-modal="true" aria-label={t('What I know')} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mg-panel kn-panel">
        <div className="mg-header">
          <div className="mg-title kn-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /><path d="M9 7h7M9 11h5" /></svg>
            <span>{t('What I know')}</span>
          </div>
          {tot && (
            <div className="kn-totals">
              <span><b>{tot.model_years}</b> {t('vehicle model years')}</span>
              <span><b>{tot.manuals}</b> {t('manuals')}</span>
              <span><b>{fmt(tot.pages)}</b> {t('pages')}</span>
              <span><b>{fmt(tot.graph_nodes)}</b> {t('graph entities')}</span>
              <span><b>{fmt(tot.graph_edges)}</b> {t('relationships')}</span>
            </div>
          )}
          <div className="mg-header__actions">
            <button type="button" className="cv-icon-btn" aria-label={t('Close')} title={t('Close')} onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
        <div className="kn-body">
          {loading && <div className="mg-state"><span className="cv-spinner" /> {t('Loading…')}</div>}
          {error && <div className="mg-state mg-state--error">{error}</div>}
          {data && (
            <>
              <div className="kn-caps">
                {CAPABILITIES.map(([a, b]) => (
                  <div key={a} className="kn-cap"><b>{t(a)}</b><span>{t(b)}</span></div>
                ))}
              </div>
              <div className="kn-tabs" role="tablist">
                {sets.map((s) => {
                  const key = `${s.model}|${s.year}`;
                  return (
                    <button key={key} type="button" role="tab" aria-selected={key === active} className={key === active ? 'is-active' : ''} onClick={() => setActive(key)}>
                      <span className="kn-tab__model">{uiModel(s.model)} {s.year}</span>
                      <span className="kn-tab__meta">{fmt(s.pages)} {t('pages')}</span>
                    </button>
                  );
                })}
              </div>
              {set && <SetView key={active} set={set} onAsk={(s, q) => { onAsk(uiModel(s.model), s.year, q); onClose(); }} t={t} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function KnowledgeButton({ onAsk, selectedModel, selectedYear, translate = (s) => s }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="start-new-btn kn-open-btn" onClick={() => setOpen(true)}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /><path d="M9 7h7M9 11h5" /></svg>
        <span className="btn-text">{translate('What I know')}</span>
      </button>
      {open && <Panel onClose={() => setOpen(false)} onAsk={onAsk} selectedModel={selectedModel} selectedYear={selectedYear} t={translate} />}
    </>
  );
}
